import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";
import { 
  Code2,
  Send,
  Bot,
  Package,
  HelpCircle,
  FileText,
  Link as LinkIcon,
  RefreshCw,
  Brain,
  Users,
  Mail,
  Calendar,
  Video
} from "lucide-react";

// Import brand logos
import hubspotLogo from "@/assets/logos/hubspot.svg";
import whatsappLogo from "@/assets/logos/whatsapp.svg";
import shopifyLogo from "@/assets/logos/shopify.svg";
import typo3Logo from "@/assets/logos/typo3.svg";
import zapierLogo from "@/assets/logos/zapier.svg";
import pipedriveLogo from "@/assets/logos/pipedrive.svg";
import woocommerceLogo from "@/assets/logos/woocommerce.svg";
import microsoft365Logo from "@/assets/logos/microsoft365.svg";
import googleMeetLogo from "@/assets/logos/googlemeet.svg";

interface IntegrationMeta {
  id: string;
  logo?: string;
  logoMaxWidth?: number;
  icon?: React.ElementType;
  bgColor: string;
  featureIcons: React.ElementType[];
  comingSoon?: boolean;
}

const integrationsMeta: IntegrationMeta[] = [
  { id: "hubspot", logo: hubspotLogo, bgColor: "bg-[#ff7a59]/10", featureIcons: [RefreshCw, Users, Send] },
  { id: "whatsapp", logo: whatsappLogo, bgColor: "bg-[#25d366]/10", featureIcons: [Users, Send, Bot] },
  { id: "shopify", logo: shopifyLogo, bgColor: "bg-[#96bf48]/10", featureIcons: [Package, HelpCircle, Brain] },
  { id: "typo3", logo: typo3Logo, logoMaxWidth: 120, bgColor: "bg-[#ff8700]/10", featureIcons: [FileText, RefreshCw, FileText] },
  { id: "zapier", logo: zapierLogo, bgColor: "bg-[#ff4a00]/10", featureIcons: [LinkIcon, RefreshCw, RefreshCw], comingSoon: true },
  { id: "microsoft365", logo: microsoft365Logo, bgColor: "bg-[#0078d4]/10", featureIcons: [Calendar, RefreshCw, Users] },
  { id: "google-meet", logo: googleMeetLogo, bgColor: "bg-[#00897b]/10", featureIcons: [Video, Calendar, RefreshCw] },
  { id: "pipedrive", logo: pipedriveLogo, bgColor: "bg-[#017737]/10", featureIcons: [RefreshCw, Users, Brain] },
  { id: "woocommerce", logo: woocommerceLogo, bgColor: "bg-[#96588a]/10", featureIcons: [Package, HelpCircle, Brain] },
  { id: "smtp", icon: Mail, bgColor: "bg-blue-500/10", featureIcons: [Send, Users, RefreshCw] },
  { id: "rest-api", icon: Code2, bgColor: "bg-primary/10", featureIcons: [RefreshCw, FileText, Brain] },
];

const IntegrationenGrid = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { tObject } = useLanguage();

  const translatedIntegrations = tObject<Array<{ name: string; description: string; features: string[] }>>("integrationenPage.grid.integrations");

  return (
    <section 
      ref={ref as React.RefObject<HTMLElement>}
      className="py-16 md:py-24 bg-secondary/30"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {integrationsMeta.map((meta, index) => {
              const data = translatedIntegrations[index];
              if (!data) return null;
              return (
                <div
                  id={meta.id}
                  key={meta.id}
                  className={cn(
                    "group bg-background rounded-2xl p-6 border border-border hover:border-chathero-mint/50 hover:shadow-lg transition-all duration-500 scroll-mt-32",
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  )}
                  style={{ transitionDelay: isVisible ? `${index * 100}ms` : "0ms" }}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-14 flex items-center">
                      {meta.logo ? (
                        <img 
                          src={meta.logo} 
                          alt={`${data.name} logo`}
                          className="h-10 w-auto object-contain"
                          style={meta.logoMaxWidth ? { maxWidth: meta.logoMaxWidth } : undefined}
                        />
                      ) : meta.icon ? (
                        <div className={cn("w-14 h-14 rounded-xl flex items-center justify-center", meta.bgColor)}>
                          <meta.icon className="w-7 h-7 text-primary" />
                        </div>
                      ) : null}
                    </div>
                    {meta.comingSoon && (
                      <Badge variant="secondary" className="text-xs">
                        Coming Soon
                      </Badge>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-primary mb-2">
                    {data.name}
                  </h3>

                  {/* Description */}
                  <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                    {data.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-2">
                    {data.features.map((feature, idx) => {
                      const FeatureIcon = meta.featureIcons[idx] || RefreshCw;
                      return (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <FeatureIcon className="w-4 h-4 text-chathero-mint flex-shrink-0" />
                          <span className="text-foreground/80">{feature}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntegrationenGrid;
